<template>
  <div ref="menuRoot" class="language-switcher">
    <button
      class="control-btn"
      type="button"
      :aria-label="t('languageSwitcher.label')"
      :aria-expanded="isOpen"
      @click="isOpen = !isOpen"
    >
      <BaseIcon iconName="globe" />
    </button>

    <div v-if="isOpen" class="language-menu" role="menu">
      <template v-for="(language, index) in languages" :key="language.code">
        <button
          class="language-option"
          :class="{ selected: language.code === locale }"
          type="button"
          role="menuitemradio"
          :aria-checked="language.code === locale"
          @click="changeLocale(language.code)"
        >
          {{ language.label }}
        </button>
        <div v-if="index === 0" class="language-divider" />
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { setLocale } from '@/i18n'
import BaseIcon from '@/components/BaseComponents/BaseIcon.vue'

const { locale, t } = useI18n()
const store = useStore()
const isOpen = ref(false)
const menuRoot = ref(null)
const languageOptions = [
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'pt-BR', label: 'Português (Brasil)' },
  { code: 'ru', label: 'Русский' },
  { code: 'tr', label: 'Türkçe' },
  { code: 'ja', label: '日本語' },
  { code: 'zh', label: '简体中文' },
  { code: 'ko', label: '한국어' },
]

const languages = computed(() => [
  ...languageOptions.filter((language) => language.code === locale.value),
  ...languageOptions.filter((language) => language.code !== locale.value),
])

function changeLocale(language) {
  setLocale(language)
  isOpen.value = false
  store.dispatch('syncLocalePreference')
}

function closeOnOutsideClick(event) {
  if (!menuRoot.value?.contains(event.target)) isOpen.value = false
}

onMounted(() => document.addEventListener('click', closeOnOutsideClick))
onBeforeUnmount(() => document.removeEventListener('click', closeOnOutsideClick))
</script>

<style scoped>
.language-switcher {
  position: relative;
}

.control-btn {
  box-sizing: content-box;
  padding: var(--control-btn-padding);
}

.control-btn:focus-visible,
.language-option:focus-visible {
  outline: 2px solid var(--color-white-70);
  outline-offset: 2px;
}

.language-menu {
  position: absolute;
  top: calc(100% + var(--spacing-sm));
  right: 0;
  z-index: var(--z-index-dropdown);
  display: flex;
  min-width: 120px;
  padding: var(--spacing-xs);
  flex-direction: column;
  background: var(--color-background);
  border: 1px solid var(--color-white-10);
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-lg);
}

.language-option {
  padding: var(--spacing-sm) var(--spacing-md);
  color: var(--color-white-40);
  font: inherit;
  text-align: left;
  background: transparent;
  border: 0;
  border-radius: var(--border-radius-xs);
  cursor: pointer;
}

.language-option:hover,
.language-option.selected {
  color: var(--color-white-70);
  background: var(--color-white-10);
}

.language-divider {
  height: 1px;
  margin: var(--spacing-xs) 0;
  background: var(--color-white-10);
}
</style>
